import { httpsCallable as _httpsCallable, type HttpsCallableOptions, type HttpsCallableResult } from 'firebase/functions';
import { functions, auth } from '@/config/firebase';
import { API_ENDPOINTS } from '@/config/constants';
import { networkStats } from '@/lib/network-stats';
import {
  getEncryptedToken,
  setEncryptedToken,
  clearEncryptedToken,
  type EncryptedTokenKey,
} from '@/lib/api/encrypted-token';
import { sanitizeHTMLForPrompt } from '@/lib/utils/sanitize-prompt';

/**
 * Tracked `httpsCallable` wrapper — every invocation increments the CF call counter.
 * Import this instead of `firebase/functions` httpsCallable.
 *
 * NOTE: Rate limiting is NOT done here because the CF name (e.g. `new_callServiceAuthorizedAPI`)
 * is too broad — all proxy requests share the same CF, so parallel Promise.all calls
 * trigger false positives. Instead, rate limiting is handled per-URL in proxy.ts.
 */
export function httpsCallable<TReq = unknown, TRes = unknown>(
  functionsInstance: typeof functions,
  name: string,
  options?: HttpsCallableOptions
) {
  const callable = _httpsCallable<TReq, TRes>(functionsInstance, name, options);
  return (data: TReq) => {
    networkStats.cfCall();
    return callable(data);
  };
}

// Typed callable wrappers
interface ServiceAPIRequest {
  appCode: string;
  url: string;
  method: string;
  data?: unknown;
  headers?: Record<string, string>;
  resource?: string;
  /** TPA service name (e.g. 'twilio'). Required for TPA API calls so the
   *  Cloud Function knows to load TPA credentials from Firestore. */
  TPAService?: string;
  /** Admin-only: impersonate another user for debugging permissions.
   *  The Cloud Function validates that the caller is an authorized admin. */
  _ua_lic_impersonateUserID?: string;
  /** Shared admin access: use another admin's TPA credentials.
   *  Set when a user was added to an admin's "App Users" list and chose
   *  to use that admin's account. The CF loads TPA creds from admin's doc. */
  _ua_lic_adminUserID?: string;
  /** When true, the Cloud Function uses provided headers as-is instead of
   *  loading stored credentials from Firestore. Used for pre-save validation
   *  (e.g. MessageBird credential check before saving). */
  do_not_replace_creds?: boolean;
}

/**
 * Common wrapper that adds encrypted-token plumbing to any
 * `new_callServiceAuthorizedAPI` callable. Mirrors the legacy
 * firebase-things.js:8823 logic exactly:
 *  1. Before the call — inject the cached `UA-Authorization-Encoded` header
 *     for this service slot (if we have one), so the CF can short-circuit
 *     the Firestore credential read.
 *  2. After the call — if the response envelope carries an `encryptedToken`,
 *     stash it for next time. On a 401 we drop the slot so the next call
 *     forces a fresh credential read.
 *
 * `tpa` vs `saas` slot is chosen by whether the request has `TPAService`,
 * matching legacy `isTPAService`.
 */
type ServiceAPICallable = (data: ServiceAPIRequest) => Promise<HttpsCallableResult<unknown>>;

function withEncryptedTokens(rawFn: ServiceAPICallable): ServiceAPICallable {
  return async (data: ServiceAPIRequest) => {
    const tokenKey: EncryptedTokenKey = data.TPAService ? 'tpa' : 'saas';
    const existing = getEncryptedToken(tokenKey);
    // Only allocate a new headers object if we actually have a token to add —
    // keeps the wire format identical for callers that don't pass headers.
    const requestData = existing
      ? { ...data, headers: { ...(data.headers ?? {}), 'UA-Authorization-Encoded': existing } }
      : data;

    const result = await rawFn(requestData);

    const envelope = result?.data as Record<string, unknown> | null | undefined;
    if (envelope && typeof envelope === 'object') {
      const fresh = envelope.encryptedToken;
      if (typeof fresh === 'string' && fresh.length > 0) {
        setEncryptedToken(tokenKey, fresh);
      }
      // 401 → server signalled the cached cred is stale; flush so next call
      // re-loads from Firestore (legacy did the same at line 8861).
      const code = (envelope.code ?? envelope.status) as number | string | undefined;
      if (code === 401) {
        clearEncryptedToken(tokenKey);
      }
    }
    return result;
  };
}

const _rawCallServiceAPI = httpsCallable<ServiceAPIRequest, unknown>(
  functions,
  API_ENDPOINTS.SERVICE_API
);

export const callServiceAPI: ServiceAPICallable = withEncryptedTokens(
  _rawCallServiceAPI as ServiceAPICallable,
);

/**
 * Calls the Cloud Function through the local emulator via direct HTTP.
 * Used ONLY by Calendly TPA proxy calls in local dev so the emulator's
 * updated OAuth token-refresh logic is used, while all other integrations
 * (Twilio, HubSpot, etc.) continue hitting production.
 *
 * In production builds this falls back to the standard `callServiceAPI`.
 *
 * We can't use a second `getFunctions()` instance because Firebase returns
 * the same singleton per (app, region), and creating a separate Firebase app
 * wouldn't share the Auth session (httpsCallable needs the user's ID token).
 * Instead we make a direct fetch() to the emulator endpoint with the token.
 */
const EMULATOR_URL = `http://localhost:5001/ulgebra-license/us-central1/${API_ENDPOINTS.SERVICE_API}`;

export const callServiceAPILocal: ServiceAPICallable = import.meta.env.DEV
  ? withEncryptedTokens(async (data: ServiceAPIRequest) => {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(EMULATOR_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ data }),
      });
      const json = await response.json();
      // httpsCallable wraps result in { data: ... }, emulator HTTP does too
      return { data: json.result ?? json.data ?? json } as HttpsCallableResult<unknown>;
    })
  : callServiceAPI;

export const makePipedriveCall = httpsCallable<unknown, unknown>(
  functions,
  API_ENDPOINTS.PIPEDRIVE_CALL
);

export const callProxyRequest = httpsCallable<unknown, unknown>(
  functions,
  API_ENDPOINTS.PROXY_REQUEST
);

// Generic helper for calling any Cloud Function.
// `options.timeout` (ms) overrides the SDK's 70s client-side deadline — pass it
// for long-running callables (the server can run up to 540s).
export async function callFunction<TReq = unknown, TRes = unknown>(
  name: string,
  data: TReq,
  options?: HttpsCallableOptions
): Promise<TRes> {
  const fn = httpsCallable<TReq, TRes>(functions, name, options);
  const result = await fn(data) as HttpsCallableResult<TRes>;
  return result.data;
}

/**
 * Get webhook authentication token for the current user.
 * This token is used in webhook URLs to authenticate incoming requests.
 * Mirrors the original app's getPersonalWebhookAuthtoken function.
 */
interface WebhookAuthResponse {
  authtoken: {
    tpa: string;
    saas: string;
  };
}

export async function getWebhookAuthToken(appCode: string): Promise<WebhookAuthResponse['authtoken'] | null> {
  try {
    const { useAuthStore } = await import('@/stores/auth-store');
    const { impersonatedUserId } = useAuthStore.getState();

    const fn = httpsCallable<Record<string, unknown>, unknown>(
      functions,
      API_ENDPOINTS.SERVICE_API
    );
    const payload: Record<string, unknown> = {
      appCode,
      action: 'GET_WEBHOOK_AUTH_TOKEN',
      ...(impersonatedUserId ? { _ua_lic_impersonateUserID: impersonatedUserId } : {}),
    };
    const result = await fn(payload) as HttpsCallableResult<WebhookAuthResponse>;

    if (result.data?.authtoken) {
      return result.data.authtoken;
    }
    return null;
  } catch (error) {
    console.error('[Functions] Failed to get webhook auth token:', error);
    return null;
  }
}

/**
 * Mint (or fetch) the user's single Automations identity token. Unlike the per-app
 * webhook token, this identifies the USER across all their apps and is what the
 * customer pastes when connecting Ulgebra in Zapier.
 */
export async function getAutomationsIdentityToken(): Promise<string | null> {
  try {
    const { useAuthStore } = await import('@/stores/auth-store');
    const { impersonatedUserId } = useAuthStore.getState();

    const fn = httpsCallable<Record<string, unknown>, { status?: string; token?: string }>(
      functions,
      API_ENDPOINTS.SERVICE_API
    );
    const payload: Record<string, unknown> = {
      action: 'GET_AUTOMATIONS_IDENTITY_TOKEN',
      ...(impersonatedUserId ? { _ua_lic_impersonateUserID: impersonatedUserId } : {}),
    };
    const result = (await fn(payload)) as HttpsCallableResult<{ status?: string; token?: string }>;
    if (result.data?.status === 'success' && result.data.token) {
      return result.data.token;
    }
    return null;
  } catch (error) {
    console.error('[Functions] Failed to get Automations identity token:', error);
    return null;
  }
}

interface WebhookTokenUserResponse {
  status: string;
  userResponse: {
    name: string;
    email: string;
  };
}

export async function getUserDetailsByWebhookToken(
  appCode: string,
  token: string,
  tokenType: 'tpa' | 'saas' = 'tpa'
): Promise<{ name: string; email: string } | null> {
  try {
    console.log('[getUserDetailsByWebhookToken] Calling with:', { appCode, tokenType, token: token.substring(0, 10) + '...' });

    const fn = httpsCallable<
      { appCode: string; action: string; ulgebra_webhook_authtoken: string; tokenType: string },
      WebhookTokenUserResponse
    >(functions, 'new_callServiceAuthorizedAPI');

    const result = await fn({
      appCode,
      action: 'GET_USERDETAILS_FOR_WEBHOOKTOKEN',
      ulgebra_webhook_authtoken: token,
      tokenType,
    });

    console.log('[getUserDetailsByWebhookToken] Full result:', result);
    console.log('[getUserDetailsByWebhookToken] result.data:', result.data);

    // Fix: Cloud Function returns { status, userResponse } directly in result.data, not result.data.data
    if (result.data?.status === 'success' && result.data?.userResponse) {
      console.log('[getUserDetailsByWebhookToken] ✓ Found user:', result.data.userResponse.name);
      return result.data.userResponse;
    }

    console.log('[getUserDetailsByWebhookToken] ✗ No user data in expected format');
    return null;
  } catch (error) {
    console.error('[getUserDetailsByWebhookToken] Error:', error);
    return null;
  }
}
/**
 * Call the AI Action Cloud Function.
 *
 * Strips HTML from `prompt` / `kbQuery` / `kbConversation` before sending.
 * Ticket content from Zoho Desk arrives as Gmail HTML, which bloats the
 * payload (10-50x), pollutes embeddings, and makes DevTools requests
 * unreadable. The server runs the same sanitizer as the authoritative
 * boundary — this client-side pass is for payload hygiene only.
 */
export async function callAIAction<TReq extends object, TRes>(
  data: TReq
): Promise<TRes> {
  const sanitized = { ...(data as Record<string, unknown>) };
  if (typeof sanitized.prompt === 'string') {
    sanitized.prompt = sanitizeHTMLForPrompt(sanitized.prompt);
  }
  if (typeof sanitized.kbQuery === 'string') {
    sanitized.kbQuery = sanitizeHTMLForPrompt(sanitized.kbQuery);
  }
  if (typeof sanitized.kbConversation === 'string') {
    sanitized.kbConversation = sanitizeHTMLForPrompt(sanitized.kbConversation);
  }
  return callFunction<TReq, TRes>(API_ENDPOINTS.AI_ACTION, sanitized as TReq);
}

